import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, FileText, Plus, Sparkles, Loader2, Search, Trash2, Edit3,
  Eye, EyeOff, RefreshCw, Globe, Zap,
} from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { TOPIC_IDEAS } from "./blog/topicIdeas";

const StatusPill = ({ status }) => {
  const cfg = status === "published"
    ? { label: "Gepubliceerd", tint: "bg-emerald-50 text-emerald-600" }
    : { label: "Concept", tint: "bg-slate-100 text-slate-500" };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold ${cfg.tint}`}>{cfg.label}</span>;
};

const AdminBlog = () => {
  useSeo({ title: "Admin — Blog CMS", robots: "noindex,nofollow" });
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const pickIdea = (idea) => {
    // Persist idea so AdminBlogEdit can prefill without a giant URL
    sessionStorage.setItem("wa_blog_idea", JSON.stringify(idea));
    navigate("/admin/blog/new?idea=" + idea.id);
  };

  const load = () => {
    setData(null);
    api("/admin/blog").then(setData).catch(() => setData({ posts: [], total: 0, published: 0, drafts: 0 }));
  };
  useEffect(() => { load(); }, []);

  const doPublish = async (p) => {
    setBusy(true);
    try {
      await api(`/admin/blog/${p.id}/publish`, { method: "POST" });
      toast.success("Gepubliceerd");
      load();
    } catch (e) { toast.error(e.message || "Mislukt"); } finally { setBusy(false); }
  };
  const doUnpublish = async (p) => {
    setBusy(true);
    try {
      await api(`/admin/blog/${p.id}/unpublish`, { method: "POST" });
      toast.success("Teruggezet naar concept");
      load();
    } catch (e) { toast.error(e.message || "Mislukt"); } finally { setBusy(false); }
  };
  const doDelete = async (p) => {
    if (!window.confirm(`Post "${p.title}" definitief verwijderen?`)) return;
    setBusy(true);
    try {
      await api(`/admin/blog/${p.id}`, { method: "DELETE" });
      toast.success("Verwijderd");
      load();
    } catch (e) { toast.error(e.message || "Mislukt"); } finally { setBusy(false); }
  };

  const posts = (data?.posts || []).filter((p) => {
    if (filter === "draft" && p.status !== "draft") return false;
    if (filter === "published" && p.status !== "published") return false;
    if (q && !(p.title.toLowerCase().includes(q.toLowerCase()) || (p.tags || []).some((t) => t.toLowerCase().includes(q.toLowerCase())))) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6" data-testid="admin-blog">
      <div className="mx-auto max-w-6xl">
        <Link to="/admin" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-navy">
          <ArrowLeft size={14} /> Terug naar Admin
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-navy" data-testid="admin-blog-title">Blog CMS</h1>
            <p className="mt-1 text-sm text-slate-500">
              Genereer AI-artikelen en beheer wat er live staat op je blog.
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" className="rounded-xl">
              <Link to="/blog" target="_blank"><Globe size={16} className="mr-2" /> Bekijk live blog</Link>
            </Button>
            <Button asChild className="rounded-xl bg-navy text-amber-300 hover:bg-navy/90" data-testid="new-post-btn">
              <Link to="/admin/blog/new"><Sparkles size={16} className="mr-2" /> Nieuwe post (AI)</Link>
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-5 grid grid-cols-3 gap-3">
          <Stat label="Totaal" value={data?.total ?? "—"} tint="bg-brandblue-light text-brandblue" />
          <Stat label="Gepubliceerd" value={data?.published ?? "—"} tint="bg-emerald-50 text-emerald-600" />
          <Stat label="Concepten" value={data?.drafts ?? "—"} tint="bg-slate-100 text-slate-500" />
        </div>

        {/* Actuele post-ideeën (curated) */}
        <div className="mt-8">
          <div className="flex items-baseline justify-between">
            <div>
              <h2 className="font-display text-lg font-bold text-navy">💡 Actuele post-ideeën</h2>
              <p className="text-xs text-slate-500">Klik op een kaart → AI schrijft direct met de meegeleverde bronnen. Publish met één klik.</p>
            </div>
            <span className="text-[11px] font-semibold text-slate-400">{TOPIC_IDEAS.length} kant-en-klaar</span>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {TOPIC_IDEAS.map((idea) => (
              <button
                type="button"
                key={idea.id}
                data-testid={`idea-${idea.id}`}
                onClick={() => pickIdea(idea)}
                className="group flex h-full flex-col rounded-2xl border border-border bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-brandblue hover:shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <span className="text-2xl">{idea.emoji}</span>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                    idea.accent === "danger" ? "bg-red-50 text-danger" :
                    idea.accent === "warning" ? "bg-amber-50 text-amber-600" :
                    idea.accent === "success" ? "bg-emerald-50 text-emerald-600" :
                    "bg-brandblue-light text-brandblue"
                  }`}>
                    <Zap size={10} /> ACTUEEL
                  </span>
                </div>
                <h3 className="mt-3 font-display text-sm font-extrabold leading-snug text-navy line-clamp-2 group-hover:text-brandblue">
                  {idea.title}
                </h3>
                <p className="mt-1 line-clamp-3 text-xs leading-relaxed text-slate-500">
                  {idea.topic}
                </p>
                <div className="mt-3 flex flex-wrap gap-1 text-[10px]">
                  {idea.tags.slice(0, 3).map((t) => (
                    <span key={t} className="rounded-full bg-slate-100 px-1.5 py-0.5 font-bold text-slate-500">#{t}</span>
                  ))}
                </div>
                <div className="mt-auto flex items-center justify-between pt-3 text-[11px] text-slate-400">
                  <span>{idea.source_urls.length} bronnen · ~{idea.target_length_words} woorden</span>
                  <span className="inline-flex items-center gap-1 font-bold text-brandblue transition group-hover:translate-x-0.5">
                    Genereer <Sparkles size={11} />
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Zoek op titel of tag…"
              className="rounded-full pl-9"
              data-testid="blog-search"
            />
          </div>
          <div className="inline-flex rounded-full bg-white p-1 shadow-sm">
            {[["all", "Alles"], ["published", "Live"], ["draft", "Concept"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`blog-filter-${k}`}
                className={`rounded-full px-3 py-1 text-xs font-bold ${filter === k ? "bg-navy text-white" : "text-slate-500"}`}>{l}</button>
            ))}
          </div>
          <Button variant="outline" onClick={load} className="rounded-full"><RefreshCw size={14} className="mr-1" /> Ververs</Button>
        </div>

        {/* Table */}
        <div className="mt-5 rounded-3xl border border-border bg-white shadow-sm">
          {data === null ? (
            <div className="flex items-center justify-center p-10 text-slate-400"><Loader2 className="animate-spin" size={22} /></div>
          ) : posts.length === 0 ? (
            <div className="p-10 text-center">
              <FileText size={28} className="mx-auto text-slate-300" />
              <p className="mt-3 text-sm font-semibold text-slate-500">Nog geen posts</p>
              <p className="mt-1 text-xs text-slate-400">Begin met AI en publiceer je eerste artikel binnen 2 minuten.</p>
              <Button asChild className="mt-4 rounded-full bg-navy text-amber-300 hover:bg-navy/90">
                <Link to="/admin/blog/new"><Plus size={14} className="mr-1" /> Nieuwe post</Link>
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {posts.map((p) => (
                <div key={p.id} className="flex items-start gap-3 p-4" data-testid={`post-${p.slug}`}>
                  {p.hero_image_url ? (
                    <img src={p.hero_image_url} alt="" className="h-16 w-24 shrink-0 rounded-xl object-cover" />
                  ) : (
                    <div className="h-16 w-24 shrink-0 rounded-xl bg-slate-100" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="min-w-0 truncate font-bold text-navy">{p.title}</h3>
                      <StatusPill status={p.status} />
                      {p.ai_generated && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">AI</span>}
                    </div>
                    <p className="mt-0.5 line-clamp-1 text-xs text-slate-500">{p.subtitle || p.meta_description || "—"}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                      <span>/blog/{p.slug}</span>
                      <span>·</span>
                      <span>{p.read_minutes || 1} min</span>
                      <span>·</span>
                      <span>{p.view_count || 0} views</span>
                      {(p.tags || []).slice(0, 3).map((t) => (
                        <span key={t} className="rounded-full bg-slate-100 px-1.5 py-0.5">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button asChild variant="outline" size="sm" className="rounded-full" data-testid={`edit-${p.slug}`}>
                      <Link to={`/admin/blog/${p.id}`}><Edit3 size={14} /></Link>
                    </Button>
                    {p.status === "published" ? (
                      <Button variant="outline" size="sm" onClick={() => doUnpublish(p)} disabled={busy} className="rounded-full" data-testid={`unpub-${p.slug}`}>
                        <EyeOff size={14} />
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => doPublish(p)} disabled={busy} className="rounded-full text-emerald-600" data-testid={`pub-${p.slug}`}>
                        <Eye size={14} />
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => doDelete(p)} disabled={busy} className="rounded-full text-danger" data-testid={`del-${p.slug}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, tint }) => (
  <div className="rounded-2xl border border-border bg-white p-4 shadow-sm">
    <div className={`inline-flex h-8 w-8 items-center justify-center rounded-xl ${tint}`}><FileText size={14} /></div>
    <div className="mt-2 font-display text-2xl font-extrabold text-navy">{value}</div>
    <div className="mt-0.5 text-xs font-medium text-slate-500">{label}</div>
  </div>
);

export default AdminBlog;
